import React, { useMemo, useState } from 'react';

import Toggle from './Toggle/Toggle';
import Popup from '../../../../../Popup';
import EditFeaturePopup from '../EditFeaturePopup/EditFeaturePopup';

import editIcon from '../../../../../../../assets/img/edit-black.svg';

import styles from './FeaturesList.module.css';

const FeaturesList = props => {
  const {
    features,
    setFeatures,
    dataPointColors,
    setDataPointColors,
    saveUpdatedFeatures,
    questions,
    questionTypes,
    distributorGroups,
    isFiltered,
    filteredGraphFeatures,
    unFilteredFeatures,
    setUnFilteredFeatures,
    filteringOnSelection,
    activeFiltersLength,
    templateType,
    presentationModeEnabled
  } = props;

  const [editFeaturePopup, setEditFeaturePopup] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  const handleMouseOver = (e, featureIndex) => {
    setTooltipPosition({
      x: e.clientX,
      y: e.clientY,
      featureQuestions: featuresQuestions.find(
        qF => qF.featureIndex === featureIndex
      )
    });
  };

  const handleMouseOut = () => {
    setTooltipPosition(null);
  };

  const getFeatureResultsCount = (xQuestionId, yQuestionId) => {
    const xQuestion = questions.find(q => q.id === xQuestionId);
    const yQuestion = questions.find(q => q.id === yQuestionId);

    const featureCounts = {
      results: 0,
      unfilteredResults: 0
    };
    if (xQuestion && yQuestion) {
      // Take smallest number in case two questions have different amount of results
      featureCounts.results =
        xQuestion.results.length < yQuestion.results.length
          ? xQuestion.results.length
          : yQuestion.results.length;

      if (isFiltered) {
        const xCount = filteringOnSelection
          ? xQuestion.unfilteredResults.length - xQuestion.results.length
          : xQuestion.unfilteredResults.length;

        const yCount = filteringOnSelection
          ? yQuestion.unfilteredResults.length - yQuestion.results.length
          : yQuestion.unfilteredResults.length;

        featureCounts.unfilteredResults = xCount < yCount ? xCount : yCount;
      }
    }
    return featureCounts;
  };

  const featureResultStats = useMemo(
    () => {
      if (features && features.length && questions && questions.length) {
        return features.map(feature =>
          getFeatureResultsCount(
            feature[questionTypes.xQuestion],
            feature[questionTypes.yQuestion]
          )
        );
      }
      return [];
    },
    [
      features,
      filteredGraphFeatures,
      filteringOnSelection,
      activeFiltersLength,
      questions
    ]
  );

  const onUpdateDataPointActive = (i, active, unFilteredFeature) => {
    if (unFilteredFeature) {
      const newUnFilteredGraphFeatures = [
        ...unFilteredFeatures.map(uFF => ({ ...uFF }))
      ];
      newUnFilteredGraphFeatures[i] = {
        ...newUnFilteredGraphFeatures[i],
        active
      };
      setUnFilteredFeatures(newUnFilteredGraphFeatures);
    } else {
      const newFeatures = [...features.map(feature => ({ ...feature }))];
      newFeatures[i] = {
        ...newFeatures[i],
        active
      };
      setFeatures(newFeatures);
      saveUpdatedFeatures(newFeatures);
    }
  };

  const onUpdateGroupActive = group => {
    const newActiveState = !isGroupActive(group.features);

    const newFeatures = [...features.map(feature => ({ ...feature }))];
    group.features.forEach(groupFeature => {
      newFeatures[groupFeature.featureIndex] = {
        ...newFeatures[groupFeature.featureIndex],
        active: newActiveState
      };
    });
    setFeatures(newFeatures);
    saveUpdatedFeatures(newFeatures);
    if (isFiltered) {
      const newUnFilteredGraphFeatures = [
        ...unFilteredFeatures.map(uFF => ({ ...uFF }))
      ];
      group.features.forEach(groupFeature => {
        newUnFilteredGraphFeatures[groupFeature.featureIndex] = {
          ...newUnFilteredGraphFeatures[groupFeature.featureIndex],
          active: newActiveState
        };
      });
      setUnFilteredFeatures(newUnFilteredGraphFeatures);
    }
  };

  const isGroupActive = groupFeatures => {
    if (isFiltered) {
      return [
        ...groupFeatures,
        ...unFilteredFeatures.filter((_unfilteredFeature, unfilteredIndex) =>
          groupFeatures.some(f => f.featureIndex === unfilteredIndex)
        )
      ].every(feature => feature.active);
    }
    return groupFeatures.every(feature => feature.active);
  };

  const featureResultsCountMessage = (featureCounts, unFilteredToggle) => {
    let countText = 'Total sample';
    if (unFilteredToggle && filteringOnSelection) {
      countText = 'Not selected';
    } else if (!unFilteredToggle && isFiltered) {
      countText = 'Selected';
    }

    return `${countText} (N = ${
      unFilteredToggle ? featureCounts.unfilteredResults : featureCounts.results
    })`;
  };

  const onSaveFeature = feature => {
    const newFeatures = [...features.map(f => ({ ...f }))];
    newFeatures[feature.featureIndex] = feature;
    setFeatures(newFeatures);
    saveUpdatedFeatures(newFeatures);

    if (feature.colorCode !== dataPointColors[feature.featureIndex]) {
      const newDataPointColors = [...dataPointColors];
      newDataPointColors[feature.featureIndex] = feature.colorCode;
      setDataPointColors([...newDataPointColors]);
    }
  };

  const displayBlockQuestion = blockQuestion => {
    let question;
    try {
      question = JSON.parse(blockQuestion)
        .blocks.map(draftBlock => draftBlock.text)
        .join(' ');
    } catch (error) {
      question = blockQuestion;
    }
    return question;
  };

  const featuresQuestions = useMemo(() => {
    if (features && features.length && questions && questions.length) {
      return features.reduce((questionFeatures, feature, featureIndex) => {
        const xQuestion = questions.find(
          q => q.id === feature[questionTypes.xQuestion]
        );
        const yQuestion = questions.find(
          q => q.id === feature[questionTypes.yQuestion]
        );
        if (xQuestion && yQuestion) {
          questionFeatures.push({
            featureIndex,
            xQuestion: {
              title: displayBlockQuestion(xQuestion.question),
              formattedGlobalIndex: xQuestion.formattedGlobalIndex
            },
            yQuestion: {
              title: displayBlockQuestion(yQuestion.question),
              formattedGlobalIndex: yQuestion.formattedGlobalIndex
            }
          });
        }
        return questionFeatures;
      }, []);
    }
    return [];
  }, []);

  const groupedFeatures = useMemo(
    () =>
      features.reduce(
        (ordered, feature, featureIndex) => {
          let match = false;
          const newF = {
            ...feature,
            featureIndex
          };
          if (distributorGroups && distributorGroups.length) {
            distributorGroups.forEach(group => {
              if (
                group.flows &&
                group.flows.some(
                  flow => flow === feature[questionTypes.xQuestion]
                ) &&
                group.flows.some(
                  flow => flow === feature[questionTypes.yQuestion]
                )
              ) {
                match = true;
                if (
                  ordered.groups.length &&
                  ordered.groups.some(
                    g => g.id && g.features && g.id === group.id
                  )
                ) {
                  ordered.groups
                    .find(g => g.id === group.id)
                    .features.push(newF);
                } else {
                  ordered.groups.push({
                    id: group.id,
                    branchLabel: group.branchLabel,
                    features: [newF]
                  });
                }
              }
            });
          }
          if (!match) {
            ordered.features.push(newF);
          }
          return ordered;
        },
        { groups: [], features: [] }
      ),
    [features]
  );

  const returnFeature = (feature, index) => (
    <React.Fragment key={`feature-${index.toString()}`}>
      {((presentationModeEnabled && feature.active) ||
        !presentationModeEnabled) && (
        <div className={styles.feature}>
          <span
            className={styles.circle}
            style={{ backgroundColor: dataPointColors[index] }}
          />
          <div>
            <div
              className={styles.featureDescription}
              onMouseEnter={e => {
                if (!presentationModeEnabled) {
                  handleMouseOver(e, feature.featureIndex);
                }
              }}
              onMouseLeave={() => {
                if (!presentationModeEnabled) {
                  handleMouseOut();
                }
              }}
            >
              {feature.description}
            </div>
            {!presentationModeEnabled && (
              <div className={styles.sampleSize}>
                {featureResultStats && featureResultStats[index]
                  ? featureResultsCountMessage(featureResultStats[index])
                  : 'Total sample (N = 0)'}
              </div>
            )}
          </div>
          {!presentationModeEnabled && (
            <div className={styles.right}>
              <img
                src={editIcon}
                alt="Edit"
                className={styles.editIcon}
                role="presentation"
                onClick={() => setEditFeaturePopup(feature)}
              />
              <Toggle
                checked={feature.active}
                onToggle={() => {
                  onUpdateDataPointActive(index, !feature.active, false);
                }}
              />
            </div>
          )}
        </div>
      )}
      {isFiltered &&
        unFilteredFeatures.length &&
        unFilteredFeatures[index] &&
        ((presentationModeEnabled && unFilteredFeatures[index].active) ||
          !presentationModeEnabled) && (
          <div className={styles.feature}>
            <span
              className={styles.circle}
              style={{
                backgroundColor: dataPointColors[index],
                opacity: '0.4'
              }}
            />
            <div>
              <div>{feature.description}</div>
              {!presentationModeEnabled && (
                <div className={styles.sampleSize}>
                  {featureResultStats && featureResultStats[index]
                    ? featureResultsCountMessage(
                        featureResultStats[index],
                        true
                      )
                    : 'Total sample (N = 0)'}
                </div>
              )}
            </div>
            {!presentationModeEnabled && (
              <Toggle
                checked={unFilteredFeatures[index].active}
                onToggle={() => {
                  onUpdateDataPointActive(
                    index,
                    !unFilteredFeatures[index].active,
                    true
                  );
                }}
              />
            )}
          </div>
        )}
    </React.Fragment>
  );

  const getGroupResultStats = groupFeatures =>
    featureResultStats[
      groupFeatures.reduce((prev, current) =>
        featureResultStats[prev.featureIndex] >
        featureResultStats[current.featureIndex]
          ? prev
          : current
      ).featureIndex
    ];

  return (
    <>
      {tooltipPosition && tooltipPosition.featureQuestions && (
        <div
          className={styles.tooltipContainer}
          style={
            tooltipPosition
              ? {
                  left: `${tooltipPosition.x + 15}px`,
                  top: `${tooltipPosition.y - 5}px`,
                  display: 'block'
                }
              : {}
          }
        >
          <div className={styles.questionTitle}>
            X-axis (Q
            {tooltipPosition.featureQuestions.xQuestion.formattedGlobalIndex})
          </div>
          <div className={styles.questionText}>
            {tooltipPosition.featureQuestions.xQuestion.title}
          </div>
          <div className={styles.questionTitle}>
            Y-axis (Q
            {tooltipPosition.featureQuestions.yQuestion.formattedGlobalIndex})
          </div>
          <div>{tooltipPosition.featureQuestions.yQuestion.title}</div>
        </div>
      )}
      {presentationModeEnabled ? (
        <div className={`${styles.featuresContainer} ${styles.noScrolling}`}>
          {features && features.length
            ? features.map((feature, index) => returnFeature(feature, index))
            : null}
        </div>
      ) : (
        <div className={styles.featuresContainer}>
          {groupedFeatures && groupedFeatures.groups.length
            ? groupedFeatures.groups.map(group => (
                <div className={styles.group}>
                  <div className={styles.branchLabelContainer}>
                    <span className={styles.branchLabel}>
                      {group.branchLabel}
                    </span>
                    <span className={styles.sampleSize}>
                      {featureResultStats &&
                      featureResultStats[group.features[0].featureIndex]
                        ? featureResultsCountMessage(
                            getGroupResultStats(group.features)
                          )
                        : 'Total sample (N = 0)'}
                    </span>
                    <Toggle
                      checked={isGroupActive(group.features)}
                      onToggle={() => {
                        onUpdateGroupActive(group);
                      }}
                    />
                  </div>
                  {group.features.map(feature =>
                    returnFeature(feature, feature.featureIndex)
                  )}
                </div>
              ))
            : null}
          {groupedFeatures && groupedFeatures.features.length ? (
            <div className={styles.group}>
              {groupedFeatures.groups.length ? (
                <div className={styles.branchLabelContainer}>
                  <span className={styles.branchLabel}>Other</span>
                </div>
              ) : null}
              {groupedFeatures.features.map(feature =>
                returnFeature(feature, feature.featureIndex)
              )}
            </div>
          ) : null}
        </div>
      )}
      {editFeaturePopup ? (
        <Popup
          component={
            <EditFeaturePopup
              onClose={() => setEditFeaturePopup(false)}
              questions={questions}
              templateType={templateType}
              feature={editFeaturePopup}
              features={features}
              onSaveFeature={feature => onSaveFeature(feature)}
              dataPointColors={dataPointColors}
            />
          }
          customStyles={{ padding: 'none', overflow: 'visible' }}
          onClose={() => setEditFeaturePopup(false)}
        />
      ) : null}
    </>
  );
};

export default FeaturesList;
